import { changeSearchForm, packagePriceSearch } from "../actions/search";
import { DateTime } from "luxon";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import {
  changeGuestInfo,
  changeRemarks,
  gotoBookingRemarks,
  gotoFinalBookingStep,
  gotoPaymentFailed,
  gotoMIPaymentSuccess,
  gotoPrevStep,
  idlePayment,
  loadingPayment,
  savePaymentResult,
  executeRQMIBook,
} from "../actions/book";
import { autoCompletePOI, resetAutoCompleteData } from "../actions/common";
import { TourViewDetailsHydrator } from "../umbilical_hocs/tour_view";
import { saveBookingProcessToStorage } from "../actions/book/storage";

export const PriceSearchFormHydrator = (WrappedComponent) => {
  const Comp = (props) => {
    return <WrappedComponent {...props} />;
  };
  Comp.propTypes = {
    adults: PropTypes.number.isRequired,
    childs: PropTypes.number.isRequired,
    date: PropTypes.string,
    searching: PropTypes.bool.isRequired,
    min_price_from: PropTypes.object.isRequired,
    pax_metainfo: PropTypes.object.isRequired,
    rooms: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    onRoomAdd: PropTypes.func.isRequired,
    onRoomRemove: PropTypes.func.isRequired,
    onRemoveRoomPax: PropTypes.func.isRequired,
    onAddRoomPax: PropTypes.func.isRequired,
    onChildAgeDecrease: PropTypes.func.isRequired,
    onChildAgeIncrease: PropTypes.func.isRequired,
    onAddPax: PropTypes.func.isRequired,
    onRemovePax: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
    getDisabledDate: PropTypes.func.isRequired,
    getDateValue: PropTypes.func.isRequired,
    getDefaultDate: PropTypes.func.isRequired,
  };
  const mapStateToProps = (state) => {
    const {
      date,
      adults,
      children: childs,
      searching,
      rooms,
    } = state.PriceSearchForm;

    const min_price_from = _.get(
      state.TourDetails,
      "details.min_price_from",
      {}
    );

    const pax_metainfo = _.get(state.TourDetails, "details.pax_metainfo", {});
    const {
      last_bookable_date,
      travel_calendar,
      extended_travel_calendar,
      required_guest_ages,
      room_selection_type,
    } = state?.TourDetails?.details ?? {};

    return {
      date,
      adults,
      childs,
      searching,
      min_price_from,
      pax_metainfo,
      rooms,
      last_bookable_date,
      travel_calendar,
      extended_travel_calendar,
      required_guest_ages,
      room_selection_type,
    };
  };
  const mapDispatchToProps = (dispatch) => {
    const onRoomAdd = (rooms) => ({
      rooms: [...rooms, { adults: 2, children: 0, children_ages: [] }],
      adults: rooms.reduce((a, b) => a + b.adults, 0) + 2,
      children: rooms.reduce((a, b) => a + b.children, 0),
    });
    const onRoomRemove = (rooms) => {
      if (rooms.length > 1) {
        const newRooms = [...rooms];
        newRooms.pop();
        return {
          rooms: newRooms,
          adults: newRooms.reduce((a, b) => a + b.adults, 0),
          children: newRooms.reduce((a, b) => a + b.children, 0),
        };
      }

      return {
        rooms,
        adults: rooms.reduce((a, b) => a + b.adults, 0),
        children: rooms.reduce((a, b) => a + b.children, 0),
      };
    };
    const onRemoveRoomPax = (rooms, room, roomIdx, guestType) => {
      const newRoom = {
        ...room,
        [guestType]:
          guestType === "children"
            ? room[guestType] - 1
            : room[guestType] > 1
            ? room[guestType] - 1
            : room[guestType],
      };
      if (guestType === "children") {
        newRoom.children_ages.pop();
      }

      const newRooms = _.cloneDeep(rooms);
      newRooms.splice(roomIdx, 1, newRoom);
      return {
        rooms: newRooms,
        adults: newRooms.reduce((a, b) => a + b.adults, 0),
        children: newRooms.reduce((a, b) => a + b.children, 0),
      };
    };
    const onAddRoomPax = (rooms, room, roomIdx, guestType) => {
      if (room[guestType] < 4) {
        const newRoom = {
          ...room,
          [guestType]: room[guestType] + 1,
        };
        if (guestType === "children") {
          newRoom.children_ages.push(12);
        }

        const newRooms = _.cloneDeep(rooms);
        newRooms.splice(roomIdx, 1, newRoom);
        return {
          rooms: newRooms,
          adults: newRooms.reduce((a, b) => a + b.adults, 0),
          children: newRooms.reduce((a, b) => a + b.children, 0),
        };
      }
      return rooms;
    };
    const onChildAgeDecrease = (rooms, room, roomIdx, ageIdx, age) => {
      if (age > 0) {
        const newAges = _.cloneDeep(room.children_ages);
        newAges.splice(ageIdx, 1, age - 1);
        const newRoom = {
          ...room,
          children_ages: newAges,
        };
        const newRooms = _.cloneDeep(rooms);
        newRooms.splice(roomIdx, 1, newRoom);
        return newRooms;
      }
      return rooms;
    };
    const onChildAgeIncrease = (rooms, room, roomIdx, ageIdx, age) => {
      if (age < 12) {
        const newAges = _.cloneDeep(room.children_ages);
        newAges.splice(ageIdx, 1, age + 1);
        const newRoom = {
          ...room,
          children_ages: newAges,
        };
        const newRooms = _.cloneDeep(rooms);
        newRooms.splice(roomIdx, 1, newRoom);
        return newRooms;
      }
      return rooms;
    };
    const onAddPax = (pax, pax_metainfo, totalCurrentPax) => {
      if (totalCurrentPax + 1 <= _.get(pax_metainfo, "max_pax", 0)) {
        return pax + 1;
      }
      return pax;
    };
    const onRemovePax = (pax, paxType) => {
      if (pax > 1 && paxType === "adults") {
        return pax - 1;
      } else if (pax > 0 && paxType === "children") {
        return pax - 1;
      }
      return pax;
    };
    const onDateChange = (value) =>
      DateTime.fromISO(value.toISOString()).toISODate();
    const getDisabledDate = (date) => {
      const dt = DateTime.fromISO(date.toISOString());
      const localDt = DateTime.local().plus({ days: 7 });
      return dt.startOf("day") < localDt.startOf("day");
    };
    const getDateValue = (date) =>
      date ? DateTime.fromISO(date).toJSDate() : null;
    const getDefaultDate = () => DateTime.local().plus({ days: 7 }).toJSDate();

    return {
      onChange: ({ form }) => dispatch(changeSearchForm({ form })),
      onPkgSearch: (reference) => dispatch(packagePriceSearch(reference)),
      onRoomAdd,
      onRoomRemove,
      onRemoveRoomPax,
      onAddRoomPax,
      onChildAgeDecrease,
      onChildAgeIncrease,
      onAddPax,
      onRemovePax,
      onDateChange,
      getDisabledDate,
      getDateValue,
      getDefaultDate,
    };
  };
  return connect(mapStateToProps, mapDispatchToProps)(Comp);
};

export const BookingProcessHydrator = (
  WrappedComponent,
  product_type = null
) => {
  const Comp = (props) => {
    const dispatch = useDispatch();
    const onExecuteRqMiBook = useCallback(
      ({ history, page_session_id, guestsList, remarks }) =>
        dispatch(
          executeRQMIBook({ history, page_session_id, guestsList, remarks })
        ),
      [dispatch]
    );

    useEffect(() => {
      if (!product_type) {
        return;
      }

      saveBookingProcessToStorage({ product_type });
    }, []);

    return (
      <WrappedComponent {...props} onExecuteRqMiBook={onExecuteRqMiBook} />
    );
  };
  Comp.defaultProps = {
    option_booking: false,
    autocompletedPois: [],
  };
  Comp.propTypes = {
    option_booking: PropTypes.bool.isRequired,
    step: PropTypes.number.isRequired,
    guests: PropTypes.object.isRequired,
    remarks: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    paymentIntent: PropTypes.object.isRequired,
    bookingResult: PropTypes.object.isRequired,
    paymentResult: PropTypes.object.isRequired,
    pickup: PropTypes.object.isRequired,
    address: PropTypes.string.isRequired,
    autocompletedPois: PropTypes.array.isRequired,
    customer_email: PropTypes.string, // Coming from AuthMetaHoc
    customer_stripe_id: PropTypes.string, // Coming from AuthMetaHoc
    onPrevStep: PropTypes.func.isRequired,
    onTravellerFormChange: PropTypes.func.isRequired,
    onGoToRemarks: PropTypes.func.isRequired,
    onChangeRemarks: PropTypes.func.isRequired,
    onGoToFinalBooking: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    setIdle: PropTypes.func.isRequired,
    savePayment: PropTypes.func.isRequired,
    paymentFailed: PropTypes.func.isRequired,
    onAutocompletePOI: PropTypes.func.isRequired,
    onAutocompleteReset: PropTypes.func.isRequired,
  };
  const mapStateToProps = (state) => {
    const {
      guests,
      remarks,
      step,
      paymentIntent,
      paymentResult,
      loading,
      bookingResult,
      pickup,
      address,
    } = state.BookingInformation;
    const option_booking = _.get(
      state,
      "WebsiteMetadata.metadata.use_option_hotel_booking",
      false
    );

    const autocompletedPois = state.AutocompleteData;

    return {
      option_booking,
      guests,
      step,
      remarks,
      paymentIntent,
      paymentResult,
      loading,
      bookingResult,
      pickup,
      address,
      autocompletedPois,
    };
  };
  const mapDispatchToProps = (dispatch) => {
    const debouncedDispatch = _.debounce(dispatch, 500, {
      leading: false,
      trailing: true,
    });
    return {
      onPrevStep: () => dispatch(gotoPrevStep()),
      onTravellerFormChange: (uid, formValue, travellerType) =>
        dispatch(changeGuestInfo(uid, formValue, travellerType)),
      onChangeRemarks: (formValue) => dispatch(changeRemarks(formValue)),
      onGoToRemarks: () => dispatch(gotoBookingRemarks()),
      onGoToFinalBooking: () => dispatch(gotoFinalBookingStep()),
      setLoading: () => dispatch(loadingPayment()),
      setIdle: () => dispatch(idlePayment()),
      savePayment: (paymentResult) =>
        dispatch(savePaymentResult(paymentResult)),
      paymentFailed: () => dispatch(gotoPaymentFailed()),
      miPaymentSuccess: () => dispatch(gotoMIPaymentSuccess()),
      onAutocompletePOI: (query) => debouncedDispatch(autoCompletePOI(query)),
      onAutocompleteReset: () => dispatch(resetAutoCompleteData()),
    };
  };
  return connect(
    mapStateToProps,
    mapDispatchToProps
  )(TourViewDetailsHydrator(Comp));
};

export * from "./top_countries";
export * from "./tour_view";
export * from "./currency";
export * from "./profile";
export * from "./selector";
export * from "./pkg_price_search_selectors";
export * from "./payments";
export * from "./init";
export * from "./authentication";
export * from "./price_info";
